import StorageIcon from '@mui/icons-material/Storage';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import KnowledgeDashboard from '../../../components/KnowledgeBase/KnowledgeDashboard';
import TokenUsageIndicator from '../../../components/Library/TokenUsageIndicator';
import {
  calculateUsagePercentage,
  FILE_COUNT_LIMITS,
  FILE_SIZE_LIMITS,
  formatBytes,
} from '../../../config/uploadLimits';
import { useAccount } from '../../../contexts/AccountContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { apiService } from '../../../services/api';
import { DOMAIN_ARCHETYPES } from '../../../types/knowledge';
import { getUserLibraryStorageUsage, type StorageUsage } from '../../../utils/storageValidation';
import type { TokenUsageMetrics } from '../../../utils/tokenUsageUtils';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

export default function Step3DocLibrary({ onNext, onBack }: Props) {
  const theme = useTheme();
  const { account, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();

  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [tokenUsage] = useState<TokenUsageMetrics | null>(null);
  const [isSyncingKB, setIsSyncingKB] = useState(false);

  const handleSyncAllKnowledgeBases = async () => {
    if (!account?.uid || !account?.clientId) {
      return;
    }
    setIsSyncingKB(true);
    try {
      // Collect all active archetypes from llm_settings that have target paths or external sites
      const isProcurementEnabled = account?.clientSettings?.enableProcurementModule === true;
      const activeDomains = Object.keys(DOMAIN_ARCHETYPES).filter(
        (domain) => (domain !== 'procurement' && domain !== 'rubrics') || isProcurementEnabled
      ) as (keyof typeof DOMAIN_ARCHETYPES)[];
      const allArchetypes = Array.from(
        new Set(activeDomains.flatMap((domain) => DOMAIN_ARCHETYPES[domain]))
      );
      const archetypesToSync = allArchetypes.filter((archetype) => {
        if (account.storageType !== 'sharepoint') {
          return true; // GCS archetypes are always 'connected'
        }
        // If there's a global sharepoint config, all archetypes are implicitly connected (using default path)
        if (account.sharepointConfig?.siteId && account.sharepointConfig?.driveId) {
          return true;
        }
        const llmSetting = (account.llm_settings as Record<string, unknown>)?.[archetype] as
          | {
              external_site?: { siteId?: string; driveId?: string };
              discovery?: { target_folder_paths?: string[] };
            }
          | undefined;
        return Boolean(
          (llmSetting?.external_site?.siteId && llmSetting?.external_site?.driveId) ||
            (Array.isArray(llmSetting?.discovery?.target_folder_paths) &&
              llmSetting.discovery.target_folder_paths.length > 0)
        );
      });

      if (archetypesToSync.length === 0) {
        showSnackbar('No connected Knowledge Base folders found to sync.', 'info');
        setIsSyncingKB(false);
        return;
      }

      showSnackbar(`Starting sync for ${archetypesToSync.length} connected categories...`, 'info');

      let successCount = 0;
      let failCount = 0;

      for (const archetype of archetypesToSync) {
        try {
          const payload: Parameters<typeof apiService.indexStorage>[0] = {
            userId: account.uid,
            clientId: account.clientId,
            folderPaths: [`clients/${account.clientId}/knowledge_base/${archetype}`],
            mode: archetype,
            collectionType: archetype,
          };

          if (account.storageType === 'sharepoint') {
            const llmSetting = (account.llm_settings as Record<string, unknown>)?.[archetype] as
              | {
                  external_site?: { siteId?: string; driveId?: string };
                  discovery?: { target_folder_paths?: string[] };
                }
              | undefined;
            if (llmSetting?.external_site?.siteId && llmSetting?.external_site?.driveId) {
              payload.siteId = llmSetting.external_site.siteId;
              payload.driveId = llmSetting.external_site.driveId;
            } else {
              payload.siteId = (account.sharepointConfig?.siteId ||
                account.sharepointConfig?.defaultSiteId) as string;
              payload.driveId = (account.sharepointConfig?.driveId ||
                account.sharepointConfig?.defaultDriveId) as string;
            }

            const defaultPath = `clients/${account.clientId}/knowledge_base/${archetype}`;
            payload.folderPaths = [defaultPath];

            if (
              Array.isArray(llmSetting?.discovery?.target_folder_paths) &&
              llmSetting.discovery.target_folder_paths.length > 0
            ) {
              payload.folderPaths.push(...llmSetting.discovery.target_folder_paths);
            }
          }

          const response = await apiService.indexStorage(payload);
          const resData = response.data as { status?: string; message?: string } | undefined;
          if (response.success || resData?.status === 'success') {
            successCount++;
          } else {
            failCount++;
          }
        } catch (_e) {
          failCount++;
        }
      }

      if (failCount === 0) {
        showSnackbar('All Knowledge Bases synchronized successfully!', 'success');
        await refreshUserDetails(true);
      } else if (successCount > 0) {
        showSnackbar(
          `Synchronized ${successCount} categories, but ${failCount} failed.`,
          'warning'
        );
        await refreshUserDetails(true);
      } else {
        showSnackbar('Failed to synchronize all Knowledge Bases.', 'error');
      }
    } catch (_error) {
      showSnackbar('Error during bulk synchronization.', 'error');
    } finally {
      setIsSyncingKB(false);
    }
  };

  const loadStorageUsage = useCallback(
    async (showSuccessMessage = false): Promise<void> => {
      if (!account?.uid || !account?.clientId) {
        return;
      }
      try {
        const usage = await getUserLibraryStorageUsage(account.uid, account.clientId);
        setStorageUsage(usage);
        if (showSuccessMessage) {
          showSnackbar('Storage quota synchronized', 'success');
        }
      } catch {
        showSnackbar('Failed to sync storage usage', 'error');
      }
    },
    [account?.uid, account?.clientId, showSnackbar]
  );

  useEffect(() => {
    loadStorageUsage(false);
  }, [loadStorageUsage]);

  const storagePercentage = storageUsage
    ? calculateUsagePercentage(storageUsage.totalSize, FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)
    : 0;
  const fileCountPercentage = storageUsage
    ? calculateUsagePercentage(storageUsage.totalFiles, FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL)
    : 0;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
        Upload Key Documents
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, fontSize: '1.2rem' }}>
        To get the best results, upload a <strong>Capability Statement</strong>, a{' '}
        <strong>Previous Proposal</strong>, and at least one <strong>CV</strong>. This helps the AI
        understand your team and experience. Use the "Type" dropdowns in the table below to
        categorize your files once uploaded.
      </Typography>

      <Box sx={{ mb: 4 }}>
        {storageUsage && (
          <Paper
            elevation={0}
            sx={{ p: 2, mb: 3, backgroundColor: theme.palette.greyLight, borderRadius: 3 }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorageIcon sx={{ color: theme.palette.bidlogiq.orange, fontSize: '1.2rem' }} />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}
                >
                  Storage Details
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                  Total Storage
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}
                >
                  {formatBytes(storageUsage.totalSize)} /{' '}
                  {formatBytes(FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)} ({storagePercentage}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={storagePercentage}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: theme.palette.greyContrast,
                  '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.bidlogiq.orange },
                }}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                  Total Files
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}
                >
                  {storageUsage.totalFiles} / {FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL} (
                  {fileCountPercentage}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={fileCountPercentage}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: theme.palette.greyContrast,
                  '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.bidlogiq.orange },
                }}
              />
            </Box>

            {tokenUsage && <TokenUsageIndicator metrics={tokenUsage} />}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSyncAllKnowledgeBases}
                disabled={isSyncingKB}
                startIcon={
                  isSyncingKB ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />
                }
                sx={{
                  whiteSpace: 'nowrap',
                  borderRadius: 2,
                  bgcolor: theme.palette.bidlogiq.brick,
                  color: 'white',
                  '&:hover': {
                    bgcolor: theme.palette.bidlogiq.brick,
                    opacity: 0.9,
                  },
                }}
              >
                {isSyncingKB ? 'Syncing...' : 'Sync All'}
              </Button>
            </Box>
          </Paper>
        )}

        {account?.uid && account?.clientId ? (
          <KnowledgeDashboard domain="bidding" />
        ) : (
          <Typography color="error">Missing account information to load library.</Typography>
        )}
      </Box>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button onClick={onBack} size="large" disabled={isSyncingKB}>
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          disabled={isSyncingKB}
          sx={{
            fontWeight: 'bold',
            px: 6,
            py: 1.5,
            fontSize: '1.1rem',
            backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.bidlogiq?.rust || 'primary.dark',
            },
          }}
          onClick={async () => {
            await handleSyncAllKnowledgeBases();
            onNext();
          }}
        >
          {isSyncingKB ? 'Syncing...' : 'Continue'}
        </Button>
      </Box>
    </Box>
  );
}
